import {useNavigate, useParams} from "react-router-dom";
import {Alert, Autocomplete, Box, Divider, Stack} from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {ServiceCategoryService} from "crqlar-react-common/dist/services/serviceCategory/serviceCategory.service";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {ServiceCategory} from "crqlar-react-common/dist/models/serviceCategory/serviceCategory.model";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import ImageUploadField, {
    ALL_IMAGE_UPLOAD_RATIOS
} from "crqlar-react-common/dist/components/ImageUploadField";
import Property from "crqlar-react-common/dist/components/Property";
import LocalizedTextField from "crqlar-react-common/dist/components/LocalizedTextField";
import NewStatusPicker from "crqlar-react-common/dist/components/NewStatusPicker";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {ServiceStatus} from "crqlar-react-common/dist/models/service/serviceStatus.model";
import {isRequiredTextMissing} from "src/utils/requiredField.util";

const ServiceCategoryDetails = () => {

    //region State

    useTrackScreenVisit('system service category details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()
    const sectors = useAllSectors()

    const serviceCategoryData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return ServiceCategoryService.getServiceCategoryById(id)
            } else {
                return {}
            }
        },
        create: ServiceCategoryService.createServiceCategory,
        update: ServiceCategoryService.updateServiceCategory,
        delete: (id) => ServiceCategoryService.deleteServiceCategory({id: [id]}),
        onDeleted: () => navigate('/system/serviceCategories', {replace: true}),
        onSaved: (serviceCategory, wasCreateAction, prev) => {
            if (wasCreateAction) {
                navigate(`/system/serviceCategories/${serviceCategory.id}`, {replace: true})
            }
        },
        isValid: (serviceCategory) => {
            return !!serviceCategory.name
        },
        cleanupBeforeSave: (serviceCategory) => {
            return {
                ...serviceCategory,
                name: serviceCategory.name?.trim()
            }
        }
    }, id)

    const serviceCategory = serviceCategoryData.item
    const disabled = !!serviceCategory.managedExternally
    const setServiceCategory = serviceCategoryData.setItem

    const nameMissing = !disabled && isRequiredTextMissing(serviceCategoryData, serviceCategory.name)

    //endregion Sate

    //region Handlers

    const updateField = (field: keyof ServiceCategory, value: any) => {
        setServiceCategory({...serviceCategory, [field]: value})
    }

    //endregion Handlers

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={serviceCategory.name}
            data={serviceCategoryData}
            back={() => navigate('/system/serviceCategories')}
            restrictEdit={disabled}
        />
    )

    const titleSection = (
        <Stack gap={2}>
            {
                disabled &&
                <Alert
                    variant={'outlined'}
                    severity={'warning'}
                    sx={{py: 0, lineHeight: 1, alignItems: 'center'}}
                >
                    {t('This entity is managed by an external system and thus cannot be edited here.')}
                </Alert>
            }

            <ImageUploadField
                id={'image-picker'}
                image={serviceCategory.media}
                setImage={(image) => updateField('media', image)}
                ratio={'landscape'}
                ratioOptions={ALL_IMAGE_UPLOAD_RATIOS}
                disabled={disabled}
            />
        </Stack>
    )

    const basicInfoSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Name')}
                    tooltip={t('Display label used to group services in setup and booking surfaces.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={serviceCategory.name ?? ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                        disabled={disabled}
                    />
                </Property>

                <Property
                    title={t('Status')}
                    tooltip={t('Only Active categories appear in the guest app. Draft and Active Not Bookable remain available in staff settings and are excluded from guest-facing category results. Service booking still depends on each service’s own status.')}
                >
                    <NewStatusPicker<ServiceStatus>
                        value={serviceCategory.status || ServiceStatus.DRAFT}
                        onChange={(newStatus) => updateField('status', newStatus)}
                        disabled={disabled}
                        ariaLabel={t('Status')}
                        options={[
                            {value: ServiceStatus.DRAFT, label: t('Draft')},
                            {value: ServiceStatus.ACTIVE, label: t('Active')},
                            {value: ServiceStatus.ACTIVE_NOT_BOOKABLE, label: t('Active Not Bookable')},
                        ]}
                    />
                </Property>

                <Property
                    title={t('Sectors')}
                    tooltip={t('Sectors where services in this category can be used.')}
                >
                    <Autocomplete
                        id={'sectors'}
                        multiple
                        options={sectors}
                        getOptionLabel={(option: any) => option.name || ''}
                        value={serviceCategory.sectorIds?.map(id => sectors.find(c => c.id === id)).filter(Boolean) as any[] || []}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size={'small'}
                            />
                        )}
                        fullWidth
                        onChange={(e, value) => {
                            updateField('sectorIds', value.map((v: any) => v.id))
                        }}
                        disabled={disabled}
                    />
                </Property>

                <Property
                    title={t('Market Code (SKU)')}
                    tooltip={t('Optional external or reporting code used to identify this service category outside Crqlar.')}
                >
                    <TextField
                        id={'sku'}
                        size={'small'}
                        value={serviceCategory.marketCode ?? ''}
                        onChange={(e) => updateField('marketCode', e.target.value)}
                        fullWidth
                        disabled={disabled}
                    />
                </Property>

                <Property
                    title={t('Order priority')}
                    tooltip={t('Controls the display order for this service category; lower numbers appear first.')}
                >
                    <TextField
                        id={'order-priority'}
                        size={'small'}
                        value={serviceCategory.orderPriority ?? ''}
                        onChange={(e) => updateField('orderPriority', e.target.value === '' ? undefined : parseInt(e.target.value))}
                        fullWidth
                        type={'number'}
                        disabled={disabled}
                        inputProps={{min: 0}}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const translationsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Translations')}
            </Typography>

            <Stack gap={1}>
            <Property
                title={t('Name translations')}
                tooltip={t('Localized category names shown in guest-facing content and translated interfaces.')}
            >
                <LocalizedTextField
                    id={'nameLocalized'}
                    size={'small'}
                    value={serviceCategory.nameLocalized}
                    onChange={(e) => updateField('nameLocalized', e)}
                    fullWidth
                    disabled={disabled}
                />
            </Property>

            <Property
                title={t('Description translations')}
                tooltip={t('Localized category descriptions shown where this category is presented to guests.')}
            >
                <LocalizedTextField
                    id={'descriptionLocalized'}
                    size={'small'}
                    value={serviceCategory.descriptionLocalized}
                    onChange={(e) => updateField('descriptionLocalized', e)}
                    fullWidth
                    multiline
                    rows={3}
                    disabled={disabled}
                />
            </Property>
            </Stack>
        </Stack>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete service category')}
                description={t('This permanently deletes the category. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={serviceCategoryData.saving}
                onDelete={() => serviceCategoryData.removeNow()}
            />
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {serviceCategory.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    //endregion UI

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {titleSection}
                {basicInfoSection}
                <Divider/>
                {translationsSection}

                {
                    !!serviceCategory.id &&
                    <>
                        <Divider/>
                        {dangerZoneSection}
                    </>
                }
            </Stack>
        </Box>
    );
}

export default ServiceCategoryDetails;
